import { ReminderAction, apiHolder } from 'api';
import solarLunar from 'solarlunar';
import { RRule } from 'rrule';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import { CatalogPrivilege, InviteType, PartStatus, ScheduleClazz, TimeUnit } from './data';
import { ColorMap } from './constant';
import { EnmuRecurrenceRule, REMINDER_ACTION_TEXT_MAP, getInsertRecurrenceRule } from './components/CreateBox/util';
import { getIn18Text } from 'api';
const systemApi = apiHolder.api.getSystemApi();
const lang = systemApi.getSystemLang();
moment.locale('zh-cn');
export const isCrossDay = (start, end) => !(start.year() === end.year() && start.month() === end.month() && start.day() === end.day());
export const getHoliday = (solarDate, lunarDate) => {
    const holiday = [];
    switch (`${solarDate.month}-${solarDate.date}`) {
        case '1-1':
            holiday.push(getIn18Text('YUANDAN'));
            break;
        case '5-1':
            holiday.push(getIn18Text('LAODONGJIE'));
            break;
        case '10-1':
            holiday.push(getIn18Text('GUOQINGJIE'));
            break;
        default:
            break;
    }
    switch (`${lunarDate.month}-${lunarDate.date}`) {
        case '1-1':
            holiday.push(getIn18Text('CHUNJIE'));
            break;
        case '8-15':
            holiday.push(getIn18Text('ZHONGQIUJIE'));
            break;
        case '5-5':
            holiday.push(getIn18Text('DUANWUJIE'));
            break;
        default:
            break;
    }
    return holiday;
};
export const getLunarDayStr = (date) => {
    const data = solarLunar.solar2lunar(date.getFullYear(), date.getMonth() + 1, date.getDate());
    let lunarDay = data.lDay === 1 ? data.monthCn : data.dayCn;
    const normalLunarDay = data.dayCn;
    let lunarMonth = data.monthCn;
    const holiday = getHoliday({
        month: date.getMonth() + 1,
        date: date.getDate(),
    }, {
        month: data.lMonth,
        date: data.lDay,
    });
    if (typeof data.term === 'string' && /^清明/.test(data.term)) {
        holiday.push(getIn18Text('QINGMINGJIE'));
    }
    if (holiday.length > 0) {
        lunarDay = holiday.join(' ');
    }
    let weekStr;
    if (lang === 'en') {
        const enWeekObj = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        weekStr = enWeekObj[+data.nWeek - 1] || data.ncWeek;
    }
    else {
        weekStr = data.ncWeek.replace(/星期/g, getIn18Text('ZHOU'));
    }
    return {
        solarDay: date.getDate(),
        lunarMonth,
        lunarDay,
        normalLunarDay,
        weekStr,
    };
};
export const formatDayPopoverTitle = () => getIn18Text('QUANBURICHENG');
export const getRawTime = (date) => {
    const { y, m, d, hr, min, sec } = date;
    return new Date(y, m - 1, d, hr, min, sec);
};
export const getDateTime = (date, dateTime) => ({
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    hr: (dateTime || date).getHours(),
    min: (dateTime || date).getMinutes(),
    sec: (dateTime || date).getSeconds(),
});
export const getShowClock = (date, allDay) => {
    if (allDay) {
        return getIn18Text('QUANTIAN');
    }
    const { hr, min } = date;
    return `${hr > 9 ? hr : `0${hr}`}:${min > 9 ? min : `0${min}`}`;
};
export const getDurationTag = (startMoment, _endMoment, allDay = false) => {
    const endMoment = _endMoment.clone().add(allDay ? 1 : 0, 'days');
    let diffDays = endMoment.diff(startMoment, 'days');
    const diffHours = endMoment.diff(startMoment, 'hours') - diffDays * 24;
    const diffMinutes = endMoment.diff(startMoment, 'minutes') - (diffDays * 24 + diffHours) * 60;
    if (diffDays > 999) {
        return getIn18Text('999+TIAN');
    }
    if (diffDays === 0 && allDay) {
        diffDays = 1;
    }
    return `${diffDays ? `${diffDays}天` : ''}${diffHours ? `${diffHours}小时` : ''}${diffMinutes ? `${diffMinutes}分钟` : ''}`;
};
export const getDurationText = (startDate, endDate, allDay, weekDaySuffix = true, weekDayFloat = false, timeZone) => {
    let startStr = '';
    let endStr = '';
    let affixStr = '';
    let startDateFormatStr = getIn18Text('MMYUEDD');
    let endDateFormatStr = getIn18Text('MMYUEDD');
    let affixStartStr = '';
    let affixEndStr = '';
    if (weekDaySuffix) {
        if (!weekDayFloat) {
            startDateFormatStr += '(ddd)';
            endDateFormatStr += '(ddd)';
        }
        else {
            const start = moment(startDate).startOf('day');
            const end = moment(endDate).startOf('day');
            const today = moment().startOf('day');
            const tomorrow = today.clone().add(1, 'day');
            const dayAfterTomorrow = tomorrow.clone().add(1, 'day');
            if (allDay && end.diff(start, 'days') > 0) {
                end.add(-1, 'days');
            }
            if (start.isSame(today)) {
                affixStartStr += getIn18Text('(JINTIAN)');
            }
            else if (start.isSame(tomorrow)) {
                affixStartStr += getIn18Text('(MINGTIAN)');
            }
            else if (start.isSame(dayAfterTomorrow)) {
                affixStartStr += getIn18Text('(HOUTIAN)');
            }
            else {
                startDateFormatStr += '(ddd)';
            }
            if (end.isSame(today)) {
                affixEndStr += getIn18Text('(JINTIAN)');
            }
            else if (end.isSame(tomorrow)) {
                affixEndStr += getIn18Text('(MINGTIAN)');
            }
            else if (end.isSame(dayAfterTomorrow)) {
                affixEndStr += getIn18Text('(HOUTIAN)');
            }
            else {
                endDateFormatStr += '(ddd)';
            }
        }
    }
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    if (allDay && endMoment.diff(startMoment, 'days') > 0) {
        endMoment.add(-1, 'days');
    }
    const crossDay = !endMoment.isSame(startMoment, 'day');
    const crossYear = startMoment.year() !== moment().year() || endMoment.year() !== moment().year();
    startStr = crossYear ? `${startMoment.year()}年` : '';
    endStr = crossYear && crossDay ? `${endMoment.year()}年` : '';
    startStr += startMoment.format(startDateFormatStr + ' ') + affixStartStr;
    endStr += crossDay ? endMoment.format(endDateFormatStr + ' ') + affixEndStr : '';
    affixStr = getDurationTag(startMoment, endMoment, allDay);
    if (!allDay) {
        startStr += startMoment.format('HH:mm');
        endStr += endMoment.format('HH:mm');
    }
    endStr = endStr ? ` ~ ${endStr}` : endStr;
    return {
        startStr,
        endStr,
        affixStr,
    };
};
export const isPoorSchedule = (scheduleEvent) => {
    const { scheduleInfo: { clazz }, catalogInfo: { isOwner, privilege }, } = scheduleEvent;
    return (!isOwner && ((CatalogPrivilege.BUSY === privilege && clazz === ScheduleClazz.DEFAULT) || clazz === ScheduleClazz.PRIVATE || clazz === ScheduleClazz.CONFIDENTIAL));
};
const getDisplayTitle = (scheduleEvent) => {
    var _a, _b;
    const { scheduleInfo: { summary, allDay, start }, catalogInfo, } = scheduleEvent;
    const timeStr = getShowClock(getDateTime(new Date(start)), allDay);
    const extNicknameStr = ((_a = catalogInfo === null || catalogInfo === void 0 ? void 0 : catalogInfo.belonger) === null || _a === void 0 ? void 0 : _a.extNickname) || ((_b = catalogInfo === null || catalogInfo === void 0 ? void 0 : catalogInfo.belonger) === null || _b === void 0 ? void 0 : _b.extDesc) || '';
    const prefix = timeStr + (catalogInfo && !catalogInfo.isOwner ? ` ${extNicknameStr.length > 3 ? extNicknameStr.substring(0, 3) + '...' : extNicknameStr}` : '');
    const suffix = summary || getIn18Text('WUZHUTI');
    return [prefix, catalogInfo && !catalogInfo.isOwner ? '，' : ' ', suffix];
};
export const getWeekViewEventHeight = (start, end) => {
    const eventHeight = end.diff(start, 'hour', true) * 50;
    const titleHeight = Math.round(eventHeight / 20) - 1;
    return titleHeight * 20 || 20;
};
export const getWeekViewEventDisplayTime = (start, end, allDay) => {
    const aboveHalfHour = moment(start).clone().add(30, 'minute').isSameOrBefore(moment(end));
    const isShowTime = !(allDay || !aboveHalfHour);
    return isShowTime ? `${moment(start).format('HH:mm')}-${moment(end).format('HH:mm')}` : null;
};
export const eventDataTransform = rawData => {
    var _a, _b;
    const { scheduleInfo: { start, end, allDay, id, }, catalogInfo, } = rawData;
    const startDate = moment(start);
    const endDate = moment(end);
    const isEndDateStartOfDay = endDate.isSame(endDate.clone().startOf('day'));
    const behaviorAsAllday = isCrossDay(startDate, endDate) && isCrossDay(startDate, isEndDateStartOfDay ? endDate.clone().subtract(1, 'day') : endDate);
    const weekViewEventHight = getWeekViewEventHeight(startDate, endDate);
    const titleArray = getDisplayTitle(rawData);
    return {
        id,
        allDay: !!allDay || behaviorAsAllday,
        title: titleArray.join(''),
        start: startDate.toDate(),
        end: behaviorAsAllday && !allDay && !isEndDateStartOfDay ? endDate.clone().add(1, 'day').toDate() : endDate.toDate(),
        extendedProps: {
            behaviorAsAllday,
            weekViewEventHight,
            weekViewEventTitle: (catalogInfo && !catalogInfo.isOwner ? `${((_a = catalogInfo === null || catalogInfo === void 0 ? void 0 : catalogInfo.belonger) === null || _a === void 0 ? void 0 : _a.extNickname) || ((_b = catalogInfo === null || catalogInfo === void 0 ? void 0 : catalogInfo.belonger) === null || _b === void 0 ? void 0 : _b.extDesc)}，` : '') + titleArray.slice().pop(),
            weekViewEventDispalyTime: getWeekViewEventDisplayTime(startDate, endDate, behaviorAsAllday),
            customOrder: allDay ? -1 : 0,
            highOrder: 0,
            data: rawData,
            date: {
                startDate: startDate.toDate(),
                endDate: endDate.toDate(),
            },
        },
    };
};
export const getPartTextByStatus = (status) => {
    let text = '';
    switch (status) {
        case PartStatus.EVENT_ACCEPTED:
            text = getIn18Text('JIESHOU');
            break;
        case PartStatus.EVENT_DECLINED:
            text = getIn18Text('JUJUE');
            break;
        case PartStatus.EVENT_TENTATIVE:
            text = getIn18Text('DAIDING');
            break;
        case PartStatus.EVENT_NEEDS_ACTION:
            text = getIn18Text('WEIHUIFU');
            break;
        case PartStatus.EVENT_DELEGATED:
            text = getIn18Text('WEIPAI');
            break;
        default:
            text = getIn18Text('WEIHUIFU');
            break;
    }
    return text;
};
export const getTimeUnitTextByEnum = (value) => {
    switch (value) {
        case TimeUnit.DAY:
            return getIn18Text('TIAN');
        case TimeUnit.MIN:
            return getIn18Text('FENZHONG');
        case TimeUnit.HOUR:
            return getIn18Text('XIAOSHI');
        case TimeUnit.WEEK:
            return getIn18Text('ZHOU');
        default:
            return '';
    }
};
export const getReminderText = (r, allDay) => {
    var _a;
    let reminderRule = '';
    if (allDay && r.time) {
        reminderRule = `${r.interval > 0 ? `${getIn18Text('TIQIAN')}${r.interval}` : getIn18Text('DANG')}${getTimeUnitTextByEnum(r.timeUnit)}${moment()
            .hours(((_a = r.time) === null || _a === void 0 ? void 0 : _a.hr) || 0)
            .minutes(0)
            .format('HH:mm')}`;
    }
    else {
        reminderRule = `${getIn18Text('TIQIAN')}${r.interval}${getTimeUnitTextByEnum(r.timeUnit)}`;
    }
    return `${reminderRule}${REMINDER_ACTION_TEXT_MAP[r.reminderAction || ReminderAction.EMAIL_APP]}`;
};
export const getDateTimeByForm = (date, time, allDay = false) => {
    const dt = {
        y: date.year(),
        m: date.month() + 1,
        d: date.date(),
        hr: time && !allDay ? time.hours() : 0,
        min: time && !allDay ? time.minutes() : 0,
        sec: 0,
    };
    return dt;
};
export const getDateTimeByFormWithZone = (_date, _time, allDay = false, zoneId, zoneList) => {
    let targetOffset = moment().utcOffset();
    const targetZone = zoneList.find(zone => zone.id === +zoneId);
    if (targetZone) {
        targetOffset = targetZone.totalSeconds / 60;
    }
    const date = _date.utcOffset(targetOffset);
    const time = _time ? _time.utcOffset(targetOffset) : null;
    const dt = {
        y: date.year(),
        m: date.month() + 1,
        d: date.date(),
        hr: time && !allDay ? time.hours() : 0,
        min: time && !allDay ? time.minutes() : 0,
        sec: 0,
    };
    return dt;
};
export const getDateTimeByFormWithZone59 = (_date, _time, allDay = false, zoneId, zoneList) => {
    let targetOffset = moment().utcOffset();
    const targetZone = zoneList.find(zone => zone.id === +zoneId);
    if (targetZone) {
        targetOffset = targetZone.totalSeconds / 60;
    }
    const date = _date.utcOffset(targetOffset);
    const time = _time ? _time.utcOffset(targetOffset) : null;
    if (!allDay && time && isCrossDay(date, time) && date.minutes() === 59 && time.minutes() === 0) {
        const dt = {
            y: date.year(),
            m: date.month() + 1,
            d: date.date(),
            hr: !allDay ? date.hours() : 0,
            min: !allDay ? date.minutes() : 0,
            sec: 0,
        };
        return dt;
    }
    else {
        const dt = {
            y: date.year(),
            m: date.month() + 1,
            d: date.date(),
            hr: time && !allDay ? time.hours() : 0,
            min: time && !allDay ? time.minutes() : 0,
            sec: 0,
        };
        return dt;
    }
};
export const getDateByForm = (date, time, allDay = false) => {
    let res = date.clone();
    res.hours(time && !allDay ? time === null || time === void 0 ? void 0 : time.hours() : 0);
    res.minutes(time && !allDay ? time.minutes() : 0);
    res.seconds(0);
    return res.toDate();
};
export const diffCatalogList = (list, preList) => {
    let changed = preList.length !== list.length;
    for (let index = 0; index < preList.length; index++) {
        const preCatalog = preList[index];
        const curCatalog = list.find(e => e.id === preCatalog.id);
        changed = changed || curCatalog === undefined || curCatalog.updateTime !== preCatalog.updateTime;
        if (changed) {
            break;
        }
    }
    return changed;
};
export const getCatalogPrivilegeText = (privilege) => {
    switch (privilege) {
        case CatalogPrivilege.BUSY:
            return getIn18Text('ZHIKEJIANMANGXIAN');
        case CatalogPrivilege.VIEW:
            return getIn18Text('KECHAKANRICHENG');
        case CatalogPrivilege.OPREATE:
            return getIn18Text('KEYIXINJIAN\u3001');
        default:
            return getIn18Text('WUQUANXIAN');
    }
};
export const colorGhost = (color = '') => ColorMap.get(color.toUpperCase()) || color;
const transFormRRuleFromScheduleFormData = (data) => {
    var _a;
    const pOption = {
        interval: 1,
        dtstart: getRawTime(getDateTimeByForm(data.moments.startDate, data.moments.startTime, !!data.time.allDay)),
        until: (_a = data.rruleUntil) === null || _a === void 0 ? void 0 : _a.toDate(),
    };
    switch (data.enmuRecurrenceRule) {
        case EnmuRecurrenceRule.DAYLY:
            pOption.freq = RRule.DAILY;
            break;
        case EnmuRecurrenceRule.MONTHLY:
            pOption.freq = RRule.MONTHLY;
            break;
        case EnmuRecurrenceRule.YEARLY:
            pOption.freq = RRule.YEARLY;
            break;
        case EnmuRecurrenceRule.WEEKLY:
            pOption.freq = RRule.WEEKLY;
            break;
        case EnmuRecurrenceRule.WEEKDAY:
            pOption.freq = RRule.WEEKLY;
            pOption.byweekday = [RRule.MO, RRule.TU, RRule.TH, RRule.WE, RRule.FR];
            break;
        default:
            return null;
    }
    return pOption;
};
export const createFakeScheduleEventData = (data, createTempId) => {
    const start = getRawTime(getDateTimeByForm(data.moments.startDate, data.moments.startTime, !!data.time.allDay));
    const end = getRawTime(getDateTimeByForm(data.moments.endDate, data.moments.endTime, !!data.time.allDay));
    const scheduleFakeData = {
        scheduleInfo: {
            id: createTempId.toString(),
            scheduleId: createTempId,
            allDay: data.time.allDay,
            partStat: 1,
            start: start.valueOf(),
            end: end.valueOf(),
            createTime: createTempId,
            updateTime: createTempId,
            summary: data.summary,
            location: data.location,
            clazz: data.clazz,
            transp: data.transp,
            description: data.description,
            color: data.color,
            attachments: data.attachments,
            recurIntro: '',
            recurrenceId: 0,
            reminders: [],
            creator: {
                extDesc: '',
            },
            belonger: {
                extDesc: '',
            },
            organizer: {
                extDesc: '',
            },
            inviteeType: InviteType.ORGANIZER,
            priority: 0,
            status: 2,
            sequence: 0,
            privilege: 1,
            instanceId: 0,
            catalogStatus: 0,
            uid: '',
            catalogId: 0,
        },
        catalogInfo: {
            id: '',
            uid: '',
            name: '',
            status: 1,
            subscribeStatus: 0,
            seq: 0,
            type: 1,
            publish: 1,
            privilege: 1,
            createTime: 0,
            updateTime: 0,
            color: '',
            description: 'string',
            isOwner: 1,
            belonger: {
                extDesc: '',
            },
        },
        contactInfo: [],
    };
    const baseData = eventDataTransform(scheduleFakeData);
    const rrule = transFormRRuleFromScheduleFormData(data);
    if (rrule) {
        baseData.rrule = new RRule(rrule).toString();
    }
    baseData.extendedProps.fake = !0;
    return baseData;
};
export const parseRecurrence = (recur) => {
    const recurDesc = '';
    if (!recur) {
        return recurDesc;
    }
    let freqDesc = '';
    let dayDesc = '';
    const { byMonth, byMonthDay, bySetPos } = recur;
    const byDay = (recur.byDay[0] || []).slice().sort();
    switch (recur.freq || recur.userFreq || recur.frequency) {
        case 'DAILY':
            freqDesc = getIn18Text('TIAN');
            break;
        case 'WEEKLY': {
            freqDesc = getIn18Text('ZHOU');
            if (byDay.length > 0) {
                if (byDay.slice().sort().join('') === '12345') {
                    freqDesc = getIn18Text('GEGONGZUORI');
                }
                else {
                    dayDesc = byDay
                        .slice()
                        .map(n => moment()
                        .weekday(n - 1)
                        .format('dddd'))
                        .join('、');
                }
            }
            break;
        }
        case 'MONTHLY': {
            freqDesc = getIn18Text('YUE');
            if (Array.isArray(byMonthDay) && byMonthDay.length > 0) {
                const positive = byMonthDay
                    .slice()
                    .filter(d => d > 0)
                    .sort()
                    .join('、');
                dayDesc = `${positive}日`;
                const navgtive = byMonthDay
                    .slice()
                    .filter(d => d < 0)
                    .sort()
                    .map(Math.abs)
                    .join('、');
                dayDesc += navgtive ? getIn18Text('HUODAOSHUDI') + navgtive + getIn18Text('TIAN') : '';
            }
            if (Array.isArray(bySetPos) && bySetPos.length > 0 && byDay.length > 0 && !dayDesc) {
                let setPos = getIn18Text('DI') + bySetPos.slice().join('、') + getIn18Text('GE');
                if (byDay.join('') === '67') {
                    setPos += getIn18Text('ZHOUMO');
                }
                else if (byDay.join('') === '12345') {
                    setPos += getIn18Text('GONGZUORI');
                }
                else if (byDay.join('') === '1234567') {
                    setPos += getIn18Text('ZIRANRI');
                }
                else {
                    setPos += byDay
                        .map(n => moment()
                        .weekday(n - 1)
                        .format('dddd'))
                        .join('、');
                }
                dayDesc += setPos;
            }
            break;
        }
        case 'YEARLY': {
            freqDesc = getIn18Text('NIAN');
            const [month] = byMonth;
            const [date] = byMonthDay;
            if (month && date) {
                dayDesc += moment()
                    .month(month - 1)
                    .date(date)
                    .format(getIn18Text('MMYUEDD'));
            }
            if (Array.isArray(bySetPos) && bySetPos.length > 0 && byDay.length > 0 && !dayDesc && month) {
                freqDesc += `${month}月`;
                let setPos = getIn18Text('DI') + bySetPos.slice().join('、') + getIn18Text('GE');
                if (byDay.join('') === '67') {
                    setPos += getIn18Text('ZHOUMO');
                }
                else if (byDay.join('') === '12345') {
                    setPos += getIn18Text('GONGZUORI');
                }
                else if (byDay.join('') === '1234567') {
                    setPos += getIn18Text('ZIRANRI');
                }
                else {
                    setPos += byDay
                        .map(n => moment()
                        .weekday(n - 1)
                        .format('dddd'))
                        .join('、');
                }
                dayDesc += setPos;
            }
            break;
        }
        default:
            break;
    }
    let desc = `每${recur.interval > 1 ? recur.interval : ''}${freqDesc}${dayDesc ? `的${dayDesc}` : ''}重复`;
    if (recur.count) {
        desc += ` 重复${recur.count}次`;
    }
    if (recur.until) {
        desc += ` 到${moment(getRawTime(recur.until)).format(getIn18Text('YYYYNIAN11'))}结束`;
    }
    return desc;
};
export const senderMailToOriginInvitees = (values, event, condition) => {
    var _a, _b, _c;
    const prevDay = Boolean(condition.get('autoPrev'));
    const { moments: { startDate, startTime, endDate, endTime }, time: { allDay: userAllday }, location: userLocation, enmuRecurrenceRule, rruleUntil, meetingOrderParam, attachments: userAttachments, description: uDesc, summary: uSummary, } = values;
    const { scheduleInfo: { start, end, allDay, location, recur, meetingInfo, attachments, description, summary }, } = event;
    const user = (_a = systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id;
    const isOrg = ((_c = (_b = event === null || event === void 0 ? void 0 : event.scheduleInfo) === null || _b === void 0 ? void 0 : _b.organizer) === null || _c === void 0 ? void 0 : _c.extDesc) === user;
    const userStart = getRawTime(getDateTimeByForm(startDate, startTime, !!userAllday)).valueOf();
    const userEnd = getRawTime(getDateTimeByForm(endDate, endTime, !!userAllday)).valueOf();
    const prevRRule = omit(recur, 'recurIntro');
    const _userRRule = enmuRecurrenceRule && enmuRecurrenceRule !== EnmuRecurrenceRule.NONE
        ? getInsertRecurrenceRule(values, prevDay) || Object.assign(Object.assign({}, prevRRule), { freq: recur === null || recur === void 0 ? void 0 : recur.freq, userFreq: recur === null || recur === void 0 ? void 0 : recur.userFreq })
        : {};
    const userRRule = omitBy(_userRRule, isUndefined);
    if (!isEmpty(userRRule) && rruleUntil) {
        userRRule.until = getDateTime(rruleUntil.toDate());
    }
    const userAttachmentsIds = userAttachments.map(a => a.accountAttachmentId).sort();
    const attachmentsIds = attachments.map(a => a.accountAttachmentId).sort();
    return (userStart !== start ||
        userEnd !== end ||
        !!allDay !== !!userAllday ||
        location !== userLocation ||
        (meetingOrderParam === null || meetingOrderParam === void 0 ? void 0 : meetingOrderParam.room_id) !== (meetingInfo === null || meetingInfo === void 0 ? void 0 : meetingInfo.room_id) ||
        !isEqual(prevRRule, userRRule) ||
        !isEqual(userAttachmentsIds, attachmentsIds) ||
        (isOrg && (!isEqual(uDesc, description) || uSummary !== summary)));
};
export const getWeekDayStr = (time, isShort) => {
    const weekStrObj = {
        en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        'en-short': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        zh: ['星期一', '星期二', '星期三', '星期四', '星期五', '星期六', '星期日'],
        'zh-short': ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
    };
    if (moment.isMoment(time)) {
        const index = time.isoWeekday() - 1;
        if (lang === 'en') {
            if (!!isShort) {
                return weekStrObj['en-short'][index] || '';
            }
            else {
                return weekStrObj['en'][index] || '';
            }
        }
        else {
            if (!!isShort) {
                return weekStrObj['zh-short'][index] || '';
            }
            else {
                return weekStrObj['zh'][index] || '';
            }
        }
    }
    return '';
};
